import React, { Fragment, useState, useEffect, useMemo, useCallback, useRef } from 'react';
import T from 'prop-types';
import cx from 'classnames';
import { isEqual, isPlainObject, pick, find, isEmpty, uniq, partition } from 'lodash-es';

import {
  Menu,
  SubMenu,
  MenuItem,
  MenuGroup,
  MenuHeader,
  MenuDivider,
  MenuSpacer,
  MenuButton,
  ShowOnlySelected,
  CustomLabelSwitch,
  CustomLabelCheckbox,
  SelectableSubMenuLabel,
  MenuItemGhostButton,
  RequestAccess,
  useMenuProps,
  useSubMenuProps,
  useShadowLine,
  filterBySearchQuery,
} from '../DropdownSet';
import Tooltip from '../Tooltip';
import Button from '../Buttons/Button';
import SearchField from '../Search/SearchField';

import {
  excludeInactiveCountries,
  prepareRegionsList,
  excludeWorldwide,
  getSelectedRegions,
  unpackRegions,
  packRegions,
} from './config';
import { makeCountriesOptions } from '../../utils/helpers';
import usStates, { isoStates } from '../../utils/usa_states';

const emptyArray = [];

const GeoFilterDropdown = ({
  selected,
  selectedStates = emptyArray,
  includedUnknownHQ = false,
  regionsList,
  countryOptionsWithStatus,
  enableWorldwide = true,
  enableUnknownHQ = false,
  enableUSStates,
  allowRegionSlug,
  partialAccess = false,
  requestFeature,
  // Appearance
  size,
  caret = true,
  frameless = false,
  shape,
  variant,
  position,
  menuAlign,
  disabled,
  staticButtonLabel,
  // Handlers
  makeRegionTooltip,
  decorateButtonLabel,
  onChange,
}) => {
  const splitByActive = !isEmpty(countryOptionsWithStatus);

  const [activeCountryOptions, inactiveCountryOptions] = useMemo(() => (
    partition(countryOptionsWithStatus, 'active')
  ), [countryOptionsWithStatus]);

  const memoRegionsList = useMemo(() => (splitByActive
    ? excludeInactiveCountries(regionsList, inactiveCountryOptions)
    : regionsList
  ), [regionsList, inactiveCountryOptions]);

  const {
    flattenRegionsList,
    countriesIsoByRegion,
    allCountriesOfRegions,
    regionsOptions,
  } = useMemo(() => prepareRegionsList(memoRegionsList, {
    enableWorldwide,
    partialAccess,
  }), [memoRegionsList]);

  const allCountriesIso = useMemo(() => {
    if (splitByActive) {
      const activeCountriesIso = activeCountryOptions.map(({ value }) => value);
      return uniq(allCountriesOfRegions, activeCountriesIso);
    }

    return allCountriesOfRegions;
  }, [activeCountryOptions]);

  const menuInstanceRef = useRef();
  const resetOnCloseRef = useRef(true);
  const searchInstanceRef = useRef();

  const selectedISOs = useMemo(() => (
    unpackRegions(selected, countriesIsoByRegion)
  ), [selected, countriesIsoByRegion]);

  const [localState, setLocalState] = useState({
    selected: selectedISOs,
    includedUnknownHQ,
    selectedStates,
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [showSelected, setShowSelected] = useState(false);

  const menuProps = useMenuProps({
    ...(position ? { position } : {}),
    ...(menuAlign ? { align: menuAlign } : {}),
    menuStyle: { width: 282 },
    className: 'country-filter-dropdown',
  });

  const subMenuProps = useSubMenuProps({
    menuStyle: { width: 260 },
  });

  const [shadowLine, onScroll] = useShadowLine();

  const isSearchActive = searchQuery.trim().length > 0;
  const isApplyDisabled = !localState.selected.length;
  const isWWChosen = localState.selected.includes('WW');
  const isUSChosen = localState.selected.includes('US');

  const normalizedSelected = (enableWorldwide && isWWChosen)
    ? allCountriesIso
    : localState.selected;

  const normalizeSelectedStates = enableUSStates && isUSChosen && !localState.selectedStates.length
    ? isoStates
    : localState.selectedStates;

  // Regions
  const { selectedRegions } = useMemo(() => (
    getSelectedRegions(localState.selected, { countriesIsoByRegion })
  ), [localState.selected, countriesIsoByRegion]);

  const filteredRegionsOptions = useMemo(() => (
    filterBySearchQuery(regionsOptions, searchQuery)
  ), [searchQuery, regionsOptions]);

  // Countries
  const countriesOptions = useMemo(() => (
    makeCountriesOptions(allCountriesIso, { enableUSStates })
  ), [allCountriesIso, enableUSStates]);

  const filteredCountriesOptions = useMemo(() => {
    const options = countriesOptions.reduce((result, country) => {
      if (showSelected && !normalizedSelected.includes(country.value)) return result;

      return result.concat({
        ...country,
        disabled: partialAccess && !selectedISOs.includes(country.value),
      });
    }, []);

    return filterBySearchQuery(options, searchQuery);
  }, [searchQuery, countriesOptions, showSelected]);

  const filteredInactiveCountriesOptions = useMemo(() => (
    filterBySearchQuery(inactiveCountryOptions, searchQuery)
  ), [searchQuery, inactiveCountryOptions]);

  const showDivider = filteredRegionsOptions.length > 0 && filteredCountriesOptions.length > 0;

  const handleUnknownHQInclusion = useCallback((e) => {
    e.keepOpen = true;
    setLocalState(prevState => ({ ...prevState, includedUnknownHQ: e.checked }));
  }, []);

  // Regions selection
  const handleRegionsSelection = (e) => {
    e.keepOpen = true;
    let nextRegions;

    if (e.checked) {
      if (e.value === 'worldwide') nextRegions = [e.value];
      else nextRegions = [...excludeWorldwide(selectedRegions), e.value];
    } else {
      nextRegions = selectedRegions.filter(region => region !== e.value);
    }

    const nextCountries = nextRegions.reduce((acc, region) => (
      acc.concat(countriesIsoByRegion[region])
    ), []);

    setLocalState(prevState => ({
      ...prevState,
      selected: nextCountries,
      selectedStates: [],
    }));
  };

  const handleOnlyRegionSelection = (region) => {
    setLocalState(prevState => ({
      ...prevState,
      selected: countriesIsoByRegion[region],
      selectedStates: [],
    }));
  };

  // Countries selection
  const handleCountriesSelection = useCallback((e) => {
    e.keepOpen = true;

    setLocalState((prevState) => {
      let nextCountries;

      if (e.checked) {
        nextCountries = [...excludeWorldwide(prevState.selected), e.value];

        // If all countries are selected then replace with WW
        const allCountriesSelected = nextCountries.length === allCountriesIso.length;
        if (enableWorldwide && !inactiveCountryOptions.length && allCountriesSelected) {
          nextCountries = ['WW'];
        }
      } else {
        nextCountries = (prevState.selected.includes('WW')
          ? allCountriesIso
          : prevState.selected
        ).filter(country => country !== e.value);
      }

      return { ...prevState, selected: nextCountries };
    });
  }, [allCountriesIso]);

  const handleOnlyCountrySelection = (countryIso) => {
    setLocalState(prevState => ({
      ...prevState,
      selected: [countryIso],
      selectedStates: [],
    }));
  };

  // States selection
  const handleSelectedStatesChange = (states) => {
    const withoutUSCountries = normalizedSelected.filter(countryIso => countryIso !== 'US');
    const nextCountries = states.length ? [...withoutUSCountries, 'US'] : withoutUSCountries;
    const isFullUS = states.length === isoStates.length;

    setLocalState(prevState => ({
      ...prevState,
      selected: nextCountries,
      selectedStates: isFullUS ? [] : states,
    }));
  };

  const handleStatesSelection = (e) => {
    e.keepOpen = true;

    const nextStates = e.checked
      ? [...localState.selectedStates, e.value]
      : normalizeSelectedStates.filter(usState => usState !== e.value);

    handleSelectedStatesChange(nextStates);
  };

  const handleStateSelection = (value) => {
    resetOnCloseRef.current = false;
    setLocalState(prevState => ({ ...prevState, selected: ['US'], selectedStates: [value] }));
  };

  const toggleShowSelected = useCallback((e) => {
    e.keepOpen = true;
    setShowSelected(e.checked);
  }, []);

  const programmaticallyCloseMemu = () => {
    if (menuInstanceRef.current) menuInstanceRef.current.closeMenu();
  };

  const handleCancel = () => {
    programmaticallyCloseMemu();
  };

  const handleApply = () => {
    if (isApplyDisabled) return;
    resetOnCloseRef.current = false;

    const nextSelected = allowRegionSlug
      ? packRegions(localState.selected, selectedRegions, countriesIsoByRegion)
      : localState.selected;

    onChange(nextSelected, pick(localState, ['includedUnknownHQ', 'selectedStates']));
    programmaticallyCloseMemu();
  };

  // Reset localState on close unless Apply clicked
  const handleMenuChange = ({ open }) => {
    searchInstanceRef.current.clearSearch(true);
    if (open) resetOnCloseRef.current = true;
    else if (resetOnCloseRef.current) {
      setLocalState({ selected: selectedISOs, includedUnknownHQ, selectedStates });
    }
  };

  // Effects
  useEffect(() => {
    setLocalState((prevState) => {
      const nextState = { selected: selectedISOs, includedUnknownHQ, selectedStates };
      if (isEqual(prevState, nextState)) return prevState;
      return nextState;
    });
  }, [selectedISOs, includedUnknownHQ, selectedStates]);

  // Unselect Show Only Selected on search
  useEffect(() => {
    if (isSearchActive || isWWChosen) setShowSelected(false);
  }, [isSearchActive, isWWChosen]);

  const renderRegionMenuItem = (region) => {
    const { label, value, disabled: disabledOption } = region;

    const isChecked = selectedRegions.includes(value);
    const isOnlyOneRegionChecked = selectedRegions.length === 1;
    const showOnlyRegionButton = !(value === 'worldwide' || (isOnlyOneRegionChecked && isChecked));
    const CustomLabel = value === 'worldwide' ? CustomLabelSwitch : CustomLabelCheckbox;

    return (
      <Tooltip
        key={value}
        content={makeRegionTooltip ? makeRegionTooltip(region) : null}
        placement="left"
        overlayStyle={{ maxWidth: 202 }}
      >
        <MenuItem
          value={value}
          checked={isChecked}
          type="checkbox"
          disabled={disabledOption}
          className={cx({ 'ghost-button-item': showOnlyRegionButton })}
          onClick={handleRegionsSelection}
        >
          <CustomLabel
            id={value}
            checked={isChecked}
            disabled={disabledOption}
            labelText={label}
          />
          {showOnlyRegionButton && (
            <MenuItemGhostButton
              value={value}
              onClick={handleOnlyRegionSelection}
            />
          )}
        </MenuItem>
      </Tooltip>
    );
  };

  const buttonLabel = useMemo(() => {
    if (staticButtonLabel) return staticButtonLabel;

    if (!selectedISOs.length) return 'Select Country';

    // Single region selected
    const { selectedRegions: regions } = getSelectedRegions(selectedISOs, { countriesIsoByRegion });

    if (regions.length === 1 && countriesIsoByRegion[regions[0]].length === selectedISOs.length) {
      const [selectedRegion] = regions;
      return find(flattenRegionsList, ['value', selectedRegion]).text;
    }

    // Single country selected
    if (selectedISOs.length === 1) {
      if (selectedStates.length === 1) {
        const [selectedState] = selectedStates;
        return (find(usStates, ['value', selectedState]) || {}).label;
      }

      if (selectedStates.length > 1) return 'Multiple Locations';

      const [selectedCountry] = selectedISOs;
      return (find(countriesOptions, ['value', selectedCountry]) || {}).label;
    }

    return 'Multiple Countries';
  }, [selectedISOs, countriesOptions, countriesIsoByRegion, flattenRegionsList]);

  const requestFeatureParams = useMemo(() => {
    if (isPlainObject(requestFeature)) return requestFeature;

    return {
      featureName: requestFeature,
      title: 'Access All Countries',
      description: 'Gain great insight with access to all countries and regions.',
    };
  }, [requestFeature]);

  return (
    <Menu
      menuButton={(
        <MenuButton
          size={size}
          caret={caret}
          shape={shape}
          variant={variant}
          disabled={disabled}
          frameless={frameless}
        >
          {decorateButtonLabel ? decorateButtonLabel(buttonLabel) : buttonLabel}
        </MenuButton>
      )}
      setDownOverflow
      instanceRef={menuInstanceRef}
      {...menuProps}
      onMenuChange={handleMenuChange}
    >
      <SearchField
        instanceRef={searchInstanceRef}
        onChange={setSearchQuery}
      />
      {shadowLine}
      <MenuGroup takeOverflow onScroll={onScroll}>
        {enableUnknownHQ && (
          <MenuItem
            type="checkbox"
            checked={localState.includedUnknownHQ}
            onClick={handleUnknownHQInclusion}
          >
            <CustomLabelCheckbox
              id="include-unknown-hq"
              checked={localState.includedUnknownHQ || false}
              labelText="Include Unknown HQ"
            />
          </MenuItem>
        )}

        {showDivider && <MenuHeader>Regions</MenuHeader>}

        {filteredRegionsOptions.map(renderRegionMenuItem)}

        {showDivider && <MenuDivider />}
        {showDivider && <MenuHeader>Countries</MenuHeader>}

        {!isSearchActive && (
          <ShowOnlySelected
            checked={showSelected}
            disabled={isWWChosen || !normalizedSelected.length}
            onClick={toggleShowSelected}
          />
        )}
        {filteredCountriesOptions.map((country) => {
          const isCheckedCountry = normalizedSelected.includes(country.value);
          const isOnlyOneCountryChecked = normalizedSelected.length === 1;
          const showOnlyCountryButton = !(isCheckedCountry && isOnlyOneCountryChecked);

          if (enableUSStates && country.children) {
            return (
              <SubMenu
                key={country.value}
                label={(
                  <Fragment>
                    <SelectableSubMenuLabel
                      label={country.label}
                      selected={normalizeSelectedStates}
                      subMenus={isoStates}
                      disabled={country.disabled}
                      onChange={handleSelectedStatesChange}
                    />
                    {showOnlyCountryButton && (
                      <MenuItemGhostButton
                        value={country.value}
                        onClick={handleOnlyCountrySelection}
                      />
                    )}
                  </Fragment>
                )}
                disabled={country.disabled}
                {...subMenuProps}
              >
                {country.children.map(({ label, value }) => {
                  const isCheckedState = normalizeSelectedStates.includes(value);
                  const isOnlyOneStateChecked = isOnlyOneCountryChecked && isUSChosen
                    && localState.selectedStates.length === 1;
                  const showOnlyStateButton = !(isCheckedState && isOnlyOneStateChecked);

                  return (
                    <MenuItem
                      key={value}
                      type="checkbox"
                      value={value}
                      checked={isCheckedState}
                      className={cx({ 'ghost-button-item': showOnlyStateButton })}
                      onClick={handleStatesSelection}
                    >
                      <CustomLabelCheckbox
                        id={value}
                        checked={isCheckedState}
                        labelText={label}
                      />
                      {showOnlyStateButton && (
                        <MenuItemGhostButton
                          value={value}
                          onClick={handleStateSelection}
                        />
                      )}
                    </MenuItem>
                  );
                })}
              </SubMenu>
            );
          }

          return (
            <MenuItem
              key={country.value}
              type="checkbox"
              value={country.value}
              checked={isCheckedCountry}
              disabled={country.disabled}
              className={cx({ 'ghost-button-item': showOnlyCountryButton })}
              onClick={handleCountriesSelection}
            >
              <CustomLabelCheckbox
                id={country.value}
                checked={isCheckedCountry}
                disabled={country.disabled}
                labelText={country.label}
              />
              {showOnlyCountryButton && (
                <MenuItemGhostButton
                  value={country.value}
                  onClick={handleOnlyCountrySelection}
                />
              )}
            </MenuItem>
          );
        })}

        {!showSelected && filteredInactiveCountriesOptions.length > 0 && (
          <Fragment>
            <MenuDivider />
            {filteredInactiveCountriesOptions.map(country => (
              <Tooltip
                key={country.value}
                content={country.tooltip || 'No data available for this country'}
                placement="left"
                overlayStyle={{ maxWidth: 202 }}
              >
                <MenuItem
                  type="checkbox"
                  value={country.value}
                  checked={false}
                  disabled
                >
                  <CustomLabelCheckbox
                    id={country.value}
                    checked={false}
                    disabled
                    labelText={country.label}
                  />
                </MenuItem>
              </Tooltip>
            ))}
          </Fragment>
        )}
        <MenuSpacer />
      </MenuGroup>
      {partialAccess ? (
        <Fragment>
          <MenuDivider />
          <RequestAccess
            featureName={requestFeatureParams.featureName}
            title={requestFeatureParams.title}
            description={requestFeatureParams.description}
          />
        </Fragment>
      ) : (
        <div className="footer-control-buttons">
          <Button
            variant="secondary"
            onClick={handleCancel}
          >
            Cancel
          </Button>
          <Button
            disabled={isApplyDisabled}
            onClick={handleApply}
          >
            Apply
          </Button>
        </div>
      )}
    </Menu>
  );
};

GeoFilterDropdown.propTypes = {
  selected: T.array.isRequired,
  selectedStates: T.array,
  includedUnknownHQ: T.bool,
  regionsList: T.array.isRequired,
  countryOptionsWithStatus: T.arrayOf(
    T.shape({
      label: T.string,
      value: T.string,
      active: T.bool,
    }),
  ),
  enableWorldwide: T.bool,
  enableUnknownHQ: T.bool,
  enableUSStates: T.bool,
  allowRegionSlug: T.bool,
  partialAccess: T.bool,
  requestFeature: T.oneOfType([
    T.string,
    T.shape({
      featureName: T.string,
      title: T.string,
      description: T.string,
    }),
  ]),
  // Appearance
  size: T.oneOf(['lg']),
  caret: T.bool,
  frameless: T.bool,
  shape: T.string,
  variant: T.string,
  position: T.string,
  menuAlign: T.string,
  disabled: T.bool,
  staticButtonLabel: T.node,
  // Handlers
  decorateButtonLabel: T.func,
  makeRegionTooltip: T.func,
  onChange: T.func.isRequired,
};

export default GeoFilterDropdown;
